import {
  WIDGET_CLIENTS_HBAR,
  WIDGET_CLIENTS_LIST,
  WIDGET_HOURS_LINE,
  WIDGET_LABELS_HBAR,
  WIDGET_LABELS_LIST,
  WIDGET_LINKED_WORK_LIST,
  WIDGET_PROJECTS_HBAR,
  WIDGET_PROJECTS_LIST,
  WIDGET_TEAMS_LIST,
  WIDGET_USERS_HBAR,
  WIDGET_USERS_LIST,
} from 'constants/widgets';
import type { HoursFilterScope } from 'redux/modules/superreports.types';
import type { Features } from 'types/account';
import type { HourColumn } from './export';

// Query limits
export const HOURS_PAGE_LIMIT = 50;

// Report columns
export const COLUMN_NAME = 'name';
// When sorting by name, the hours should sort on day instead
export const COLUMN_NAME_FALLBACK_FOR_HOURS = 'day';
export const COLUMN_LOGGED_HOURS = 'logged_hours';
export const COLUMN_LOGGED_MONEY = 'logged_money';
export const COLUMN_LOGGED_PERCENTAGE = 'logged_percentage';
export const COLUMN_ESTIMATED_HOURS = 'estimated_hours';
export const COLUMN_ESTIMATED_MONEY = 'estimated_money';
export const COLUMN_BILLED_MONEY = 'billed_money';
export const COLUMN_BILLED_HOURS = 'billed_hours';
export const COLUMN_BILLED = 'billed';
export const COLUMN_NOTES = 'notes';
export const COLUMN_DATE = 'date';
export const COLUMN_TAGS = 'tags';
export const COLUMN_TIMESTAMPS = 'timestamps';
export const COLUMN_UNBILLED_MONEY = 'unbilled_money';
export const COLUMN_UNBILLED_HOURS = 'unbilled_hours';
export const COLUMN_BUDGET = 'budget';
export const COLUMN_BUDGET_REMAINING = 'budget_remaining';
export const COLUMN_LAST_ACTIVITY = 'last_activity';
export const COLUMN_PROJECT = 'project';
export const COLUMN_PROJECT_DESCRIPTION = 'description';
export const COLUMN_CLIENT = 'client';
export const COLUMN_DAY = 'day';
export const COLUMN_BILLABLE = 'billable';
export const COLUMN_BILLABLE_HOURS = 'billable_hours';
export const COLUMN_NON_BILLABLE_HOURS = 'non_billable_hours';
export const COLUMN_INTERNAL_COST = 'internal_cost';
export const COLUMN_ESTIMATED_INTERNAL_COST = 'estimated_internal_cost';
export const COLUMN_CAPACITY = 'capacity';
export const COLUMN_HOURLY_RATE = 'rate';
export const COLUMN_OVERTIME = 'overtime';
export const COLUMN_UNDERTIME = 'undertime';
export const COLUMN_TAG_HEADERS = 'tag_headers';
export const COLUMN_USER_CUSTOM_PROPERTIES = 'user_custom_properties';
export const COLUMN_STATE = 'state';
export const COLUMN_PROFIT = 'profit';
export const COLUMN_PROFITABILITY = 'profitability';
export const COLUMN_DONUT_PERCENTAGE = 'donut_percentage';

// Linked work column constants
export const COLUMN_LINKED_WORK_ID = 'linked_work_id';
export const COLUMN_LINKED_WORK_TITLE = 'linked_work_title';
export const COLUMN_LINKED_WORK_STATUS = 'linked_work_status';
export const COLUMN_LINKED_WORK_JIRA_CUSTOM_FIELD = 'linked_work_jira_custom_field';

export const LOW_DONUT_THRESHOLD = 0.005;
export const COLUMNS = [
  COLUMN_NAME,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_LOGGED_PERCENTAGE,
  COLUMN_BILLED_HOURS,
  COLUMN_BILLED_MONEY,
  COLUMN_UNBILLED_HOURS,
  COLUMN_UNBILLED_MONEY,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_BILLED,
  COLUMN_NOTES,
  COLUMN_DATE,
  COLUMN_TAGS,
  COLUMN_TIMESTAMPS,
  COLUMN_BUDGET,
  COLUMN_BUDGET_REMAINING,
  COLUMN_PROJECT,
  COLUMN_DAY,
  COLUMN_BILLABLE,
  COLUMN_BILLABLE_HOURS,
  COLUMN_NON_BILLABLE_HOURS,
  COLUMN_INTERNAL_COST,
  COLUMN_PROFIT,
  COLUMN_PROFITABILITY,
  COLUMN_ESTIMATED_INTERNAL_COST,
  COLUMN_DONUT_PERCENTAGE,
  COLUMN_CAPACITY,
  COLUMN_OVERTIME,
  COLUMN_UNDERTIME,
  COLUMN_STATE,
] as const;

export type Column = (typeof COLUMNS)[number];

export const LINE_COLUMNS = [COLUMN_LOGGED_MONEY];

export const CLIENT_ONLY_COLUMNS = [
  COLUMN_NAME,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_LOGGED_PERCENTAGE,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_BILLED,
];

// Timespans
export const TIMESPAN_CUSTOM = 'custom';
export const TIMESPAN_DAY = 'day';
export const TIMESPAN_WEEK = 'week';
export const TIMESPAN_MONTH = 'month';
export const TIMESPAN_YEAR = 'year';

export const TIMESPANS = [TIMESPAN_CUSTOM, TIMESPAN_WEEK, TIMESPAN_MONTH, TIMESPAN_YEAR];

export const COLUMN_NAMES = {
  [COLUMN_NAME]: 'Name',
  [COLUMN_LOGGED_HOURS]: 'Logged hours',
  [COLUMN_LOGGED_MONEY]: 'Logged money',
  [COLUMN_LOGGED_PERCENTAGE]: 'Logged hours %',
  [COLUMN_ESTIMATED_HOURS]: 'Planned hours',
  [COLUMN_ESTIMATED_MONEY]: 'Planned money',
  [COLUMN_BILLED_MONEY]: 'Billed money',
  [COLUMN_BILLED_HOURS]: 'Billed hours',
  [COLUMN_BILLED]: 'Billed',
  [COLUMN_NOTES]: 'Notes',
  [COLUMN_DATE]: 'Date',
  [COLUMN_TAGS]: 'Tags',
  [COLUMN_TIMESTAMPS]: 'Hour timestamps',
  [COLUMN_UNBILLED_MONEY]: 'Unbilled money',
  [COLUMN_UNBILLED_HOURS]: 'Unbilled hours',
  [COLUMN_BUDGET]: 'Budget',
  [COLUMN_BUDGET_REMAINING]: 'Remaining budget',
  [COLUMN_LAST_ACTIVITY]: 'Last activity',
  [COLUMN_CAPACITY]: 'Capacity',
  [COLUMN_HOURLY_RATE]: 'Hourly rate',
  [COLUMN_DAY]: 'Details',
  [COLUMN_PROJECT]: 'Project',
  [COLUMN_PROJECT_DESCRIPTION]: 'Project description',
  [COLUMN_BILLABLE]: 'Billable',
  [COLUMN_BILLABLE_HOURS]: 'Billable hours',
  [COLUMN_NON_BILLABLE_HOURS]: 'Non-billable hours',
  [COLUMN_INTERNAL_COST]: 'Logged cost',
  [COLUMN_ESTIMATED_INTERNAL_COST]: 'Planned cost',
  [COLUMN_DONUT_PERCENTAGE]: 'Show percentages',
  [COLUMN_OVERTIME]: 'Overtime',
  [COLUMN_UNDERTIME]: 'Undertime',
  [COLUMN_STATE]: 'State',
  [COLUMN_PROFIT]: 'Profit',
  [COLUMN_PROFITABILITY]: 'Profitability',
  [COLUMN_LINKED_WORK_ID]: 'Linked Work Item ID',
  [COLUMN_LINKED_WORK_TITLE]: 'Linked Work Item Title',
  [COLUMN_LINKED_WORK_STATUS]: 'Linked Work Item Sync Status',
  [COLUMN_LINKED_WORK_JIRA_CUSTOM_FIELD]: 'Jira Custom Field',
};

/**
 * By default COLUMN_NAMES are used as labels in widget options, but some widgets
 * have unique labels and those should be defined here
 */
export const WIDGET_OPTION_COLUMN_LABELS: Record<string, string> = {
  [COLUMN_LOGGED_PERCENTAGE]: 'Logged hours (% of selection)',
  [COLUMN_PROFIT]: 'Profit (Logged money - Logged cost)',
  [COLUMN_PROFITABILITY]: 'Profitability (Profit / Logged money)%',
} as const;

/**
 * Columns tied to certain features
 */
export const FEATURE_GATED_COLUMNS: { [_K in keyof Features]: Array<Column> } = {
  internal_costs: [COLUMN_INTERNAL_COST, COLUMN_PROFIT, COLUMN_PROFITABILITY],
};

export const WIDGET_COLUMNS_MAP = {
  [WIDGET_USERS_LIST]: COLUMNS,
  [WIDGET_USERS_HBAR]: COLUMNS,
  [WIDGET_LABELS_LIST]: COLUMNS,
  [WIDGET_LABELS_HBAR]: COLUMNS,
  [WIDGET_PROJECTS_LIST]: COLUMNS,
  [WIDGET_PROJECTS_HBAR]: COLUMNS,
  [WIDGET_CLIENTS_LIST]: CLIENT_ONLY_COLUMNS,
  [WIDGET_CLIENTS_HBAR]: CLIENT_ONLY_COLUMNS,
  [WIDGET_HOURS_LINE]: LINE_COLUMNS,
};

export const WIDGET_TYPE_TO_QUERY_FILTER_MAP: Record<string, HoursFilterScope> = {
  [WIDGET_CLIENTS_HBAR]: 'project_ids',
  [WIDGET_CLIENTS_LIST]: 'project_ids', // can't click directly on a client, behave as project widget
  [WIDGET_USERS_HBAR]: 'user_ids',
  [WIDGET_USERS_LIST]: 'user_ids',
  [WIDGET_LABELS_LIST]: 'label_ids',
  [WIDGET_LABELS_HBAR]: 'label_ids',
  [WIDGET_LINKED_WORK_LIST]: 'external_link_filter',
  [WIDGET_PROJECTS_LIST]: 'project_ids',
  [WIDGET_PROJECTS_HBAR]: 'project_ids',
  [WIDGET_TEAMS_LIST]: 'team_ids',
};

export const ITEMS_WITH_NO_TABLE_COLUMN = [
  COLUMN_NOTES,
  COLUMN_DATE,
  COLUMN_TAGS,
  COLUMN_TIMESTAMPS,
  COLUMN_BILLED,
];

export const DEFAULT_DONUT_AND_TIMELINE_COLUMNS = [
  COLUMN_NAME,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_BILLED_HOURS,
  COLUMN_BILLED_MONEY,
  COLUMN_UNBILLED_HOURS,
  COLUMN_UNBILLED_MONEY,
  COLUMN_BILLABLE_HOURS,
  COLUMN_NON_BILLABLE_HOURS,
];

export const DEFAULT_TABLE_COLUMNS = [
  COLUMN_NAME,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_DATE,
  COLUMN_NOTES,
  COLUMN_TAGS,
  COLUMN_TIMESTAMPS,
  COLUMN_BILLED,
  COLUMN_INTERNAL_COST,
  COLUMN_ESTIMATED_INTERNAL_COST,
];

export const COLUMNS_FOR_HOURS_DATA = [
  COLUMN_LOGGED_HOURS,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_BILLED_HOURS,
  COLUMN_UNBILLED_HOURS,
  COLUMN_BILLABLE_HOURS,
  COLUMN_NON_BILLABLE_HOURS,
];

export const COLUMNS_FOR_MONEY_DATA = [
  COLUMN_LOGGED_MONEY,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_BILLED_MONEY,
  COLUMN_UNBILLED_MONEY,
];

export const NOT_SEEN_YET = 'Never';

export const PROGRESS_BAR_COLUMNS = [COLUMN_BUDGET];

export const DONUT_COLUMNS = [COLUMN_DONUT_PERCENTAGE];

export const BILLABLE_COLUMNS: ReadonlyArray<Column | HourColumn> = [
  COLUMN_BILLABLE_HOURS,
  COLUMN_NON_BILLABLE_HOURS,
];

// Some columns have different names when exporting reports
export const COLUMN_EXPORT_ALIASES = {
  [COLUMN_ESTIMATED_HOURS]: 'planned_hours',
  [COLUMN_ESTIMATED_MONEY]: 'planned_money',
} as const;

/** Columns that are only supported with a single currency. */
export const SINGLE_CURRENCY_COLUMNS = [COLUMN_LOGGED_MONEY, COLUMN_ESTIMATED_MONEY];

/** Columns that are only supported with capacity reporting. */
export const CAPACITY_COLUMNS = [COLUMN_CAPACITY, COLUMN_OVERTIME, COLUMN_UNDERTIME];

/** Default state names for users without Hour States feature */
export const HOUR_STATE_LOGGED = 'Logged';
export const HOUR_STATE_BILLED = 'Billed';
export const HOUR_STATE_LOCKED = 'Locked';
export const HOUR_STATE_BILLED_AND_LOCKED = 'Billed and Locked';
export const HOUR_STATE_INVOICED = 'Invoiced';

export const PDF_RESTRICTED_COLUMNS = [
  COLUMN_INTERNAL_COST,
  COLUMN_ESTIMATED_INTERNAL_COST,
  COLUMN_PROFITABILITY,
];
